// src/features/quality/pages/InspectionRecordsPage.tsx

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePermission } from "@/auth/usePermission";


import { useV1InspectionsList, v1InspectionsList } from "@/api/generated/v1/v1";
import { PageBanner } from "@/shared/components/PageBanner";
import { DataTableToolbar } from "@/shared/components/DataTableToolbar";
import { exportToExcel, exportToPDF } from "@/shared/utils/export";

const RESULT_LABEL: Record<string, string> = {
  pass: "قبول",
  fail: "رد",
};

const TARGET_TYPE_LABEL: Record<string, string> = {
  production_order: "سفارش تولید",
  stock_transaction: "تراکنش موجودی",
};

export function InspectionRecordsPage() {
  const navigate = useNavigate();
  const canManage = usePermission("quality.manage");

  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const queryParams = { 
    page: page,
    page_size: pageSize,
    ...(searchTerm && { search: searchTerm }),
    ...activeFilters 
  };

  const { data, isLoading, isError, error } = useV1InspectionsList(queryParams as any);

  const handleFilterChange = (name: string, value: string) => {
    setActiveFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPage(1); // Reset page on filter change
  };

  const fetchExportData = async () => {
    try {
      setIsExporting(true);
      const res = await v1InspectionsList({
        export: "true",
        page_size: 1000,
        ...(searchTerm && { search: searchTerm }),
        ...activeFilters
      } as any);
      
      if ((res as any).truncated) {
        alert("تعداد رکوردها بیش از ۱۰,۰۰۰ مورد است و اکسپورت محدود شد.");
      }
      return (res as any).results || res;
    } catch (err) {
      alert("خطا در دریافت اطلاعات برای خروجی");
      throw err;
    } finally {
      setIsExporting(false);
    }
  };

  const columns = [
    { header: "نوع هدف", accessor: (row: any) => TARGET_TYPE_LABEL[row.target_type] ?? row.target_type },
    { header: "هدف بازرسی", accessor: (row: any) => row.target_display || row.target_id },
    { header: "نتیجه", accessor: (row: any) => RESULT_LABEL[row.result] ?? row.result },
    { header: "تاریخ", accessor: (row: any) => new Date(row.inspected_at as unknown as string).toLocaleDateString("fa-IR") },
    { header: "توضیحات", accessor: (row: any) => row.notes || "—" },
  ];

  const handleExportExcel = async () => {
    const rawData = await fetchExportData();
    await exportToExcel(rawData, columns, "inspection_records_export");
  };

  const handleExportPDF = async () => {
    const rawData = await fetchExportData();
    await exportToPDF(rawData, columns, "inspection_records_export");
  };

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title="سوابق بازرسی و کنترل کیفیت"
        subtitle="مشاهده و جستجو در لیست تمامی بازرسی‌های انجام‌شده در سیستم"
        icon="📋"
        helpLink="/help/quality/inspection-records"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          {canManage && (
            <button
              onClick={() => navigate("/quality/inspections/new")}
              style={{
                padding: "0.6rem 1.25rem",
                borderRadius: "12px",
                background: "#ffffff",
                color: "#007270",
                fontWeight: 700,
                fontSize: "0.9rem",
                border: "none",
                cursor: "pointer",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                transition: "all 0.2s ease",
              }}
            >
              <span>➕</span>
              <span>ثبت بازرسی جدید</span>
            </button>
          )}
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ padding: "1.5rem", maxWidth: "100%", overflowX: "auto" }}>

        <DataTableToolbar
          onSearch={(term) => {
            setSearchTerm(term);
            setPage(1); // Reset page on search
          }}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
          onExportExcel={handleExportExcel}
          onExportPDF={handleExportPDF}
          isExporting={isExporting}
          filters={[
            {
              name: "result",
              label: "نتیجه بازرسی",
              options: [
                { label: "قبول (Pass)", value: "pass" },
                { label: "رد (Fail)", value: "fail" },
              ],
            },
            {
              name: "target_type",
              label: "نوع هدف",
              options: [
                { label: "سفارش تولید", value: "production_order" },
                { label: "تراکنش موجودی", value: "stock_transaction" },
              ],
            },
          ]}
        />

        {isLoading && (
          <div className="flex-center p-8">
            <p style={{ color: "var(--color-text-muted)" }}>در حال بارگذاری اطلاعات...</p>
          </div>
        )}

        {isError && (
          <div className="p-4 mb-4" style={{ background: "rgba(255,0,0,0.1)", borderLeft: "4px solid red", borderRadius: "8px" }}>
            <p style={{ color: "red" }}>خطا در دریافت اطلاعات: {(error as Error)?.message}</p>
          </div>
        )}

        {!isLoading && !isError && data?.results && (
          <div className="table-container" style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", textAlign: "right", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--glass-border)" }}>
                  <th style={{ padding: "1rem", color: "var(--color-text-muted)" }}>نوع هدف</th>
                  <th style={{ padding: "1rem", color: "var(--color-text-muted)" }}>هدف بازرسی</th>
                  <th style={{ padding: "1rem", color: "var(--color-text-muted)" }}>نتیجه</th>
                  <th style={{ padding: "1rem", color: "var(--color-text-muted)" }}>تاریخ</th>
                  <th style={{ padding: "1rem", color: "var(--color-text-muted)" }}>توضیحات</th>
                </tr>
              </thead>
              <tbody>
                {data.results.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ padding: "2rem", textAlign: "center", color: "var(--color-text-muted)" }}>
                      هیچ سابقهٔ بازرسی‌ای یافت نشد.
                    </td>
                  </tr>
                ) : (
                  data.results.map((item) => (
                    <tr key={item.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                      <td style={{ padding: "1rem" }}>{TARGET_TYPE_LABEL[item.target_type as string] ?? item.target_type}</td>
                      <td style={{ padding: "1rem" }} dir="ltr" className="text-left font-mono text-sm">{item.target_display || item.target_id}</td>
                      <td style={{ padding: "1rem" }}>
                        <span
                          className={`font-semibold ${item.result === "pass" ? "text-green-500" : "text-red-500"}`}
                        >
                          {RESULT_LABEL[item.result as string] ?? item.result}
                        </span>
                      </td>
                      <td style={{ padding: "1rem" }}>
                        {new Date(item.inspected_at as unknown as string).toLocaleDateString("fa-IR")}
                      </td>
                      <td style={{ padding: "1rem" }}>{item.notes || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {data?.count !== undefined && (
          <div className="mt-4 pt-4 border-t flex-between" style={{ borderColor: "var(--glass-border)", color: "var(--color-text-muted)", fontSize: "0.9rem" }}>
            <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
              <span>تعداد کل: {data.count}</span>
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                <span>نمایش در صفحه:</span>
                <select 
                  className="input" 
                  style={{ padding: "0.2rem 1.5rem 0.2rem 0.5rem", width: "auto" }}
                  value={pageSize} 
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  <option value={10}>۱۰</option>
                  <option value={25}>۲۵</option>
                  <option value={50}>۵۰</option>
                  <option value={100}>۱۰۰</option>
                  <option value={1000}>همه</option>
                </select>
              </div>
            </div>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <button 
                type="button"
                className="btn btn-secondary" 
                style={{ padding: "0.2rem 0.6rem" }}
                disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                قبلی
              </button>
              <span>صفحه {page} از {Math.ceil(data.count / pageSize) || 1}</span>
              <button 
                type="button"
                className="btn btn-secondary" 
                style={{ padding: "0.2rem 0.6rem" }}
                disabled={page >= Math.ceil(data.count / pageSize)}
                onClick={() => setPage(p => p + 1)}
              >
                بعدی
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
